<script lang="ts">
	import { onMount } from "svelte";
	import Layout from "../components/Layout.svelte";
	import BlogCard from "../components/BlogCard.svelte";
	import BlogFeaturedCard from "../components/BlogFeaturedCard.svelte";
	import ContactForm from "../components/Contact/ContactForm.svelte";
	import Icon from "../components/Icon.svelte";
	import arrowRight from "../../../core/assets/icons/arrow-right.svg?raw";
	import { getTranslate } from "../utils/getTranslate.js";
	import { getTenant } from "../getTenant.js";
	import translations from "./Blog.translations.json";
	import type { BlogProps } from "./BlogProps.js";
	import { BlogCategory } from "../../../core/schema/blog/blogCategory.js";
	import {
		blogCategoryAnchorMap,
		blogCategoryNameMap,
		blogCategoryOrder,
	} from "../../../core/schema/blog/blogCategory.js";
	import { isEnum } from "../../../core/utils/enum/isEnum";

	type $$Props = BlogProps;

	const { items } = $$restProps as $$Props;

	const tenant = getTenant();
	const translate = getTranslate(translations);

	const perPage = 6;

	const categoriesPresent = blogCategoryOrder.filter((category) => items.some((item) => item.category === category));
	const categoryCounts = new Map(
		categoriesPresent.map((category) => [category, items.filter((item) => item.category === category).length]),
	);

	let selectedCategory: BlogCategory | undefined = undefined;
	let currentPage = 1;

	function selectCategory(category: BlogCategory | undefined): void {
		selectedCategory = category;
		currentPage = 1;
		const hash = category === undefined ? "" : `#${blogCategoryAnchorMap[category][tenant.locale]}`;
		history.replaceState(history.state, "", `${location.pathname}${location.search}${hash}`);
	}

	onMount(() => {
		const selectCategoryFromHash = (): void => {
			const anchor = decodeURIComponent(location.hash.slice(1));
			const category = categoriesPresent.find((category) => blogCategoryAnchorMap[category][tenant.locale] === anchor);
			selectedCategory = category;
			currentPage = 1;
		};

		selectCategoryFromHash();
		window.addEventListener("hashchange", selectCategoryFromHash);
		return (): void => {
			window.removeEventListener("hashchange", selectCategoryFromHash);
		};
	});

	$: filteredItems =
		selectedCategory === undefined ? items : items.filter((item) => item.category === selectedCategory);
	$: [featuredItem] = filteredItems;
	$: gridItems = filteredItems.slice(1);
	$: totalPages = Math.max(1, Math.ceil(gridItems.length / perPage));
	// Všechny položky zůstávají v DOM kvůli prolinkování; stránkování a filtr jen přepínají viditelnost.
	$: pageHrefs = new Set(gridItems.slice((currentPage - 1) * perPage, currentPage * perPage).map(({ href }) => href));
</script>

<Layout>
	<div class="container">
		<div class="mx-4 flex flex-col gap-8 py-16 md:mx-16 md:gap-12 md:py-24 xl:mx-32 xl:gap-16 xl:pt-32">
			<div class="flex items-center gap-4">
				<span class="bg-ltprimary-200 h-px w-8"></span>
				<span class="text-ltprimary-200 text-xs/5 font-light uppercase tracking-[0.02em] md:text-base">
					{translate("sectionTitle")}
				</span>
			</div>
			<div class="grid grid-cols-1 items-start gap-6 xl:grid-cols-2 xl:gap-32">
				<h1
					class="text-ltprimary md:text-3lg/[3.25rem] m-0 max-w-[22rem] p-0 text-[2.25rem]/[2.65rem] font-light uppercase tracking-wider md:max-w-[27rem] xl:max-w-xl xl:text-2xl/[4.125rem]"
				>
					{translate("heading")}
				</h1>
				<div
					class="text-ltsecondary md:text-md/[1.875rem] max-w-3xl text-[1.125rem]/[1.6875rem] font-light tracking-[0.02em]"
				>
					{translate("intro")}
				</div>
			</div>
		</div>

		<div class="border-lttertiary border-y">
			<div class="mx-4 md:mx-16 xl:mx-32">
				<div class="border-lttertiary flex items-center gap-3 border-x p-8 sm:hidden">
					<label for="blog-filter" class="text-2xs text-ltmeta mr-1.5 shrink-0 uppercase tracking-[0.08em]">
						{translate("filterLabel")}
					</label>
					<select
						id="blog-filter"
						class="blog-filter-select"
						value={selectedCategory ?? ""}
						on:change={({ currentTarget: { value } }) => {
							if (isEnum(BlogCategory)(value)) {
								selectCategory(categoriesPresent.find((category) => category === value));
							}
						}}
					>
						<option value="">{translate("allCategories")} ({items.length})</option>
						{#each categoriesPresent as category (category)}
							<option value={category}>
								{blogCategoryNameMap[category][tenant.locale]} ({categoryCounts.get(category)})
							</option>
						{/each}
					</select>
				</div>
				<div class="border-lttertiary hidden flex-wrap items-center gap-3 border-x p-8 sm:flex">
					<span class="text-2xs text-ltmeta mr-1.5 shrink-0 uppercase tracking-[0.08em]">
						{translate("filterLabel")}
					</span>
					<button
						type="button"
						aria-pressed={selectedCategory === undefined}
						class="flex shrink-0 items-center gap-[0.4375rem] rounded-[1.375rem] border px-[1.125rem] py-[0.5625rem] text-xs transition-colors duration-300 {selectedCategory ===
						undefined
							? 'bg-ltprimary-300 border-ltprimary-300 text-white'
							: 'border-lttertiary text-ltprimary hover:border-ltprimary-300 hover:text-ltprimary-300 bg-white'}"
						on:click={() => {
							selectCategory(undefined);
						}}
					>
						<span>{translate("allCategories")}</span>
						<span class="text-2xs opacity-70">{items.length}</span>
					</button>
					{#each categoriesPresent as category (category)}
						<button
							type="button"
							aria-pressed={selectedCategory === category}
							class="flex shrink-0 items-center gap-[0.4375rem] rounded-[1.375rem] border px-[1.125rem] py-[0.5625rem] text-xs transition-colors duration-300 {selectedCategory ===
							category
								? 'bg-ltprimary-300 border-ltprimary-300 text-white'
								: 'border-lttertiary text-ltprimary hover:border-ltprimary-300 hover:text-ltprimary-300 bg-white'}"
							on:click={() => {
								selectCategory(category);
							}}
						>
							<span>{blogCategoryNameMap[category][tenant.locale]}</span>
							<span class="text-2xs opacity-70">{categoryCounts.get(category)}</span>
						</button>
					{/each}
				</div>
			</div>
		</div>

		<section class="!my-0">
			<div class="mx-4 flex flex-col gap-10 pb-24 pt-16 md:mx-16 xl:mx-32">
				{#if featuredItem && currentPage === 1}
					<BlogFeaturedCard item={featuredItem} />
				{/if}

				<div class="grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3" class:hidden={pageHrefs.size === 0}>
					{#each items as item (item.href)}
						<div class:hidden={!pageHrefs.has(item.href)}>
							<BlogCard {item} />
						</div>
					{/each}
				</div>

				{#if filteredItems.length === 0}
					<p class="text-ltsecondary py-16 text-center">{translate("empty")}</p>
				{/if}

				{#if totalPages > 1}
					<div class="flex flex-wrap justify-center gap-2.5 pt-4">
						{#each Array.from({ length: totalPages }, (_, index) => index + 1) as page (page)}
							<button
								type="button"
								aria-current={currentPage === page ? "true" : undefined}
								class="flex size-[2.625rem] min-w-[2.625rem] items-center justify-center border text-[0.9375rem] transition-colors duration-300 {currentPage ===
								page
									? 'bg-ltprimary-300 border-ltprimary-300 text-white'
									: 'border-lttertiary text-ltprimary hover:border-ltprimary-300 hover:text-ltprimary-300 hover:bg-lttertiary-100 bg-white'}"
								on:click={() => {
									currentPage = page;
								}}
							>
								{page}
							</button>
						{/each}
						<button
							type="button"
							disabled={currentPage === totalPages}
							class="border-lttertiary text-ltprimary hover:border-ltprimary-300 hover:text-ltprimary-300 hover:bg-lttertiary-100 disabled:hover:border-lttertiary group flex size-[2.625rem] min-w-[2.625rem] items-center justify-center border bg-white transition-colors duration-300 disabled:opacity-40 disabled:hover:bg-white"
							aria-label={translate("nextPage", {}, true)}
							on:click={() => {
								if (currentPage < totalPages) {
									currentPage += 1;
								}
							}}
						>
							<Icon class="h-3.5 w-2 transition-transform duration-300 group-hover:translate-x-0.5" icon={arrowRight} />
						</button>
					</div>
				{/if}
			</div>
		</section>

		<section>
			<ContactForm />
		</section>
	</div>
</Layout>
